import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, ArrowRight, BookOpen, ChevronDown, ChevronUp, FlaskConical,
  SlidersHorizontal, CheckCircle, Info, ExternalLink, Scale, Users, Target,
  TrendingUp, Zap, GitCompare
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import api from '../lib/api';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

const ExpandableSection = ({ title, icon: Icon, iconColor, children, defaultOpen = false, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card data-testid={testId}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 text-left">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={cn("h-5 w-5", iconColor)} />}
          <span className="text-base font-semibold text-slate-900">{title}</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {open && <CardContent className="pt-0">{children}</CardContent>}
    </Card>
  );
};

const CitationLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-teal-700 hover:text-teal-900 underline underline-offset-2">
    {children}<ExternalLink className="h-3 w-3" />
  </a>
);

const FormulaBox = ({ label, formula, note, color = "indigo" }) => (
  <div className={`p-4 rounded-lg border bg-${color}-50 border-${color}-200`}>
    <p className={`text-xs font-semibold text-${color}-800 mb-2`}>{label}</p>
    <p className={`font-mono text-sm text-${color}-900 bg-white border border-slate-200 px-3 py-2 rounded border border-${color}-100`}>{formula}</p>
    {note && <p className={`text-[11px] text-${color}-600 mt-2`}>{note}</p>}
  </div>
);

export default function TransferMethodologyPage() {
  const navigate = useNavigate();
  const [effective, setEffective] = useState(null);

  useEffect(() => {
    api.get('/modelling-settings/effective').then(r => setEffective(r.data)).catch(() => {});
  }, []);

  const isRoe = effective?.effective_method === 'roe_composite';

  const __pageTheme = getLightSectionTheme('Navy');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Transfer Factor Methodology"
        description="How learning translates to behavior and business impact — the methodology, the math, and the tunable knobs."
        theme={__pageTheme}
        icon={Scale}
        testId="transfer-methodology-hero"
      />
    )}>
    <div className="px-4 md:px-8 lg:px-12 py-6 space-y-6" data-testid="transfer-methodology-page">
      {/* Header */}
      <div className="flex items-start justify-between">
                <Button variant="outline" size="sm" onClick={() => navigate(-1)} className="gap-1.5 flex-shrink-0">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
      </div>

      {/* Current Setting Banner */}
      {effective && (
        <Card className={cn("border-2", isRoe ? "border-emerald-200 bg-emerald-50/30" : "border-amber-200 bg-amber-50/30")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              {isRoe ? <FlaskConical className="h-5 w-5 text-emerald-600" /> : <SlidersHorizontal className="h-5 w-5 text-amber-600" />}
              <div>
                <span className="text-sm font-semibold text-slate-800 flex items-center flex-wrap gap-1">
                  Your organisation is currently using: <Badge className={cn("ml-1", isRoe ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700")}>{isRoe ? 'ROE Composite (Measured)' : 'Synthetic (Estimated)'}</Badge>
                </span>
                <p className="text-xs text-slate-500 mt-0.5">
                  {isRoe
                    ? `ROE Composite Factor: ${(effective.roe_composite?.factor * 100)?.toFixed(1)}% — derived from actual L2, Motivation, and L3 evaluation data.`
                    : 'Using proxy estimates based on adoption speed, leadership alignment, and operational readiness.'}
                  {' '}This can be changed in Admin {'>'} Org Defaults.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Why Transfer Matters */}
      <Card className="border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-slate-700" />
            Why Transfer is the Most Critical Factor
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-slate-700">
          <p>
            Research consistently demonstrates that <strong>only 10-20% of training investment translates into sustained behavior change</strong> on the job. This "transfer gap" is the single largest source of wasted L&D expenditure and the primary reason ROI calculations fail to reflect reality.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="p-3 bg-rose-50 border border-rose-100 rounded-lg">
              <p className="text-xs font-bold text-rose-800">Brinkerhoff (2006)</p>
              <p className="text-[10px] text-rose-600 mt-0.5">Only 15% of learners achieve full on-the-job application. 70% attempt but fail. 15% never try.</p>
              <CitationLink href="https://wearehpc.com/professor-brinkerhoffs-insights-into-learning-transfer-impact/">Source</CitationLink>
            </div>
            <div className="p-3 bg-rose-50 border border-rose-100 rounded-lg">
              <p className="text-xs font-bold text-rose-800">ATD Research (2024)</p>
              <p className="text-[10px] text-rose-600 mt-0.5">Organisations spent $1,283/employee on learning in 2023, yet most lack rigorous transfer measurement at Level 3 and beyond.</p>
              <CitationLink href="https://www.commlabindia.com/blog/kirkpatrick-philips-model-part4">Source</CitationLink>
            </div>
            <div className="p-3 bg-rose-50 border border-rose-100 rounded-lg">
              <p className="text-xs font-bold text-rose-800">Kirkpatrick Partners (2025)</p>
              <p className="text-[10px] text-rose-600 mt-0.5">"ROI is not the answer" — advocates shift from financial-only metrics to Return on Expectations integrating performance evidence.</p>
              <CitationLink href="https://www.kirkpatrickpartners.com/blog/roi-is-not-the-answer-why-learning-leaders-must-shift-to-return-on-expectations-and-performance/">Source</CitationLink>
            </div>
          </div>
          <p className="text-xs text-slate-500 italic">
            The transfer factor is the multiplier that converts <strong>theoretical impact</strong> (what could happen if 100% of learning transferred) into <strong>realistic projected impact</strong> (what we defensibly expect given real-world constraints). Getting this right is the difference between a credible business case and fiction.
          </p>
        </CardContent>
      </Card>

      {/* Two Approaches */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-slate-900">Two Approaches to Transfer Estimation</h2>
        <p className="text-sm text-slate-600">Seertech supports two methodologies. You choose which to apply at the organisation level.</p>

        {/* Approach 1: Synthetic */}
        <ExpandableSection title="Approach 1: Synthetic (Estimated)" icon={SlidersHorizontal} iconColor="text-amber-600" defaultOpen={true} testId="ref-synthetic-section">
          <div className="space-y-4 text-sm text-slate-700">
            <p>
              The synthetic method uses <strong>proxy estimates</strong> of organisational readiness to approximate how much learning will transfer. These factors are configured by the user when building a scenario, based on their best judgement of the organisation's conditions.
            </p>

            <FormulaBox
              label="Synthetic Transfer Formula"
              formula="dampening = 1 - (weighted_risk x 0.9)"
              note="weighted_risk = adoption_speed x 40% + leadership_alignment x 30% + operational_readiness x 30%"
              color="amber"
            />

            <div className="space-y-2">
              <p className="font-semibold text-slate-800">Input Factors:</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { name: 'Adoption Speed', weight: '40%', desc: 'How quickly the organisation typically adopts new practices. Risk: 0% (instant) to 100% (resistant).' },
                  { name: 'Leadership Alignment', weight: '30%', desc: 'Degree of executive and manager support for the initiative. Risk: 0% (full buy-in) to 100% (no support).' },
                  { name: 'Operational Readiness', weight: '30%', desc: 'Whether systems, processes, and infrastructure support the change. Risk: 0% (fully ready) to 100% (no infrastructure).' },
                ].map(f => (
                  <div key={f.name} className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
                    <div className="flex justify-between items-center mb-1">
                      <p className="text-xs font-bold text-amber-800">{f.name}</p>
                      <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">{f.weight}</Badge>
                    </div>
                    <p className="text-[10px] text-amber-700">{f.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <p className="text-xs font-semibold text-slate-700 mb-1">Example Calculation</p>
              <p className="text-xs text-slate-600 font-mono">
                Adoption risk = 30%, Leadership risk = 20%, Operations risk = 25%<br />
                Weighted risk = (0.30 x 0.4) + (0.20 x 0.3) + (0.25 x 0.3) = 0.255<br />
                Dampening = 1 - (0.255 x 0.9) = <strong>0.77 (77% transfer)</strong>
              </p>
            </div>

            <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-800">
                  <p className="font-semibold mb-1">When to use Synthetic:</p>
                  <ul className="space-y-0.5 list-disc list-inside text-blue-700">
                    <li>No formal ROE evaluation data collected yet</li>
                    <li>Modelling a future intervention where actuals don't exist</li>
                    <li>Quick scenario comparisons where precise measurement isn't available</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="text-xs text-slate-500">
              <p className="font-semibold mb-1">Theoretical Basis:</p>
              <p>The synthetic factors draw on organisational change management research. <CitationLink href="https://www.702010institute.com/702010-model/">The 70:20:10 model</CitationLink> established that only ~10% of learning comes from formal training — the rest depends on experiential and social factors (i.e., the transfer environment). <CitationLink href="https://immunizationacademy.com/storage/bestpractices/13_BestPractices_LearningTransfer.pdf">Brinkerhoff's 40/20/40 framework</CitationLink> allocates 40% of impact to pre-training preparation, 20% to the training event, and 40% to post-training support — reinforcing that transfer is predominantly an environmental, not instructional, problem.</p>
            </div>
          </div>
        </ExpandableSection>

        {/* Approach 2: ROE Composite */}
        <ExpandableSection title="Approach 2: ROE Composite (Measured)" icon={FlaskConical} iconColor="text-emerald-600" defaultOpen={true} testId="ref-roe-composite-section">
          <div className="space-y-4 text-sm text-slate-700">
            <p>
              The ROE Composite method <strong>replaces proxy estimates with empirical data</strong> from three validated evaluation levels. When your organisation has collected L2, Motivation, and L3 data, this method provides a defensible, evidence-based transfer factor.
            </p>

            <FormulaBox
              label="ROE Composite Formula"
              formula="Transfer Factor = (L2 x w_l2 + Motivation x w_mot + L3 x w_l3) / 100"
              note="Default weights: L2 = 25%, Motivation = 35%, L3 = 40%. Scores on 0-100 scale. Factor output: 0.05 to 1.00."
              color="emerald"
            />

            <div className="space-y-2">
              <p className="font-semibold text-slate-800">Components:</p>
              <div className="space-y-3">
                {[
                  {
                    name: 'L2: Learning Effectiveness',
                    weight: '25%',
                    desc: 'Post-assessment scores measuring knowledge and skill acquisition. Based on pre/post testing methodology.',
                    rationale: 'Necessary but insufficient — knowing something doesn\'t mean you\'ll apply it. Hence lower weight.',
                    kirkpatrick: 'Kirkpatrick Level 2',
                    cite: 'https://www.kirkpatrickpartners.com/the-kirkpatrick-model/',
                    citeLabel: 'Kirkpatrick Partners'
                  },
                  {
                    name: 'Motivation: Willingness to Apply',
                    weight: '35%',
                    desc: 'Survey-based composite of intrinsic motivation (purpose, mastery, autonomy) and extrinsic factors (recognition, career, manager support).',
                    rationale: 'The psychological bridge between learning and doing. Without motivation, even high L2 scores won\'t transfer. Research shows manager support alone drives up to 42% improvement in outcomes.',
                    kirkpatrick: 'Extends Kirkpatrick Level 1 (Reaction)',
                    cite: 'https://wearehpc.com/professor-brinkerhoffs-insights-into-learning-transfer-impact/',
                    citeLabel: 'Brinkerhoff / High Performance Consultancy'
                  },
                  {
                    name: 'L3: Observed Behavior Change',
                    weight: '40%',
                    desc: 'Manager-observed behavior change, skill application ratings, and performance improvement metrics collected 30-90 days post-training.',
                    rationale: 'The strongest predictor of business impact. L3 IS the transfer measurement — it captures what actually changed in the workplace. Hence highest weight.',
                    kirkpatrick: 'Kirkpatrick Level 3',
                    cite: 'https://www.kirkpatrickpartners.com/blog/evaluation-in-2025/',
                    citeLabel: 'Kirkpatrick Partners (2025)'
                  },
                ].map(c => (
                  <div key={c.name} className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-bold text-emerald-800">{c.name}</p>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-300">Weight: {c.weight}</Badge>
                        <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-300">{c.kirkpatrick}</Badge>
                      </div>
                    </div>
                    <p className="text-xs text-emerald-700">{c.desc}</p>
                    <p className="text-xs text-emerald-600 mt-1"><strong>Why this weight:</strong> {c.rationale}</p>
                    <p className="text-[10px] text-slate-500 mt-1"><CitationLink href={c.cite}>{c.citeLabel}</CitationLink></p>
                  </div>
                ))}
              </div>
            </div>

            {/* Live Example */}
            {effective?.roe_composite && (
              <div className="p-4 bg-white border-2 border-emerald-300 rounded-lg" data-testid="ref-live-composite">
                <p className="text-xs font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                  <Zap className="h-4 w-4" /> Live Data: Your Organisation's ROE Composite
                </p>
                <div className="grid grid-cols-3 gap-3 text-xs mb-3">
                  {Object.entries(effective.roe_composite.components).map(([key, comp]) => {
                    const labels = { roe_l2: 'L2 Effectiveness', roe_motivation: 'Motivation', roe_l3: 'L3 Behavior Transfer' };
                    return (
                      <div key={key} className="p-2 bg-emerald-50 rounded border border-emerald-100 text-center">
                        <p className="text-[10px] text-emerald-600">{labels[key]}</p>
                        <p className="text-lg font-bold text-emerald-800">{comp.score}</p>
                        <p className="text-[10px] text-slate-500">x {(comp.weight * 100).toFixed(0)}% = {(comp.contribution * 100).toFixed(1)}%</p>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between bg-emerald-100 rounded-lg p-3">
                  <span className="text-sm font-medium text-emerald-800">Composite Transfer Factor:</span>
                  <span className="text-2xl font-bold text-emerald-700">{(effective.roe_composite.factor * 100).toFixed(1)}%</span>
                </div>
              </div>
            )}

            <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-800">
                  <p className="font-semibold mb-1">When to use ROE Composite:</p>
                  <ul className="space-y-0.5 list-disc list-inside text-blue-700">
                    <li>All 3 ROE data types (L2 + Motivation + L3) have been collected for the organisation</li>
                    <li>You want defensible, evidence-based projections for stakeholder conversations</li>
                    <li>Building business cases that need to withstand CFO-level scrutiny</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-800">
                  <p className="font-semibold mb-1">Fallback Rule:</p>
                  <p className="text-amber-700">If any of the 3 ROE data types is missing, the system automatically falls back to the Synthetic method. This ensures partial data doesn't produce misleading results. All 3 components are required for a valid composite.</p>
                </div>
              </div>
            </div>
          </div>
        </ExpandableSection>
      </div>

      {/* Side-by-Side Comparison */}
      <ExpandableSection title="Side-by-Side Comparison" icon={GitCompare} iconColor="text-indigo-600" defaultOpen={true} testId="ref-comparison-section">
        <div className="space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  <th className="text-left p-3 font-semibold text-slate-700 border-b-2 border-slate-200 w-1/3">Dimension</th>
                  <th className="text-left p-3 font-semibold text-amber-700 border-b-2 border-amber-200 w-1/3">Synthetic (Estimated)</th>
                  <th className="text-left p-3 font-semibold text-emerald-700 border-b-2 border-emerald-200 w-1/3">ROE Composite (Measured)</th>
                </tr>
              </thead>
              <tbody className="text-slate-600">
                {[
                  ['Data Source', 'User judgement / best guess', 'Empirical evaluation data (L2, Motivation, L3)'],
                  ['Defensibility', 'Moderate — based on reasonable assumptions', 'High — grounded in validated assessment frameworks'],
                  ['Setup Required', 'None — available immediately', 'Requires L2 assessments, motivation surveys, L3 manager observations'],
                  ['Accuracy', 'Dependent on user expertise', 'Reflects actual organisational transfer capacity'],
                  ['Best For', 'Early-stage modelling, future interventions', 'Mature L&D functions with evaluation infrastructure'],
                  ['Stakeholder Credibility', 'Acceptable for internal planning', 'Suitable for board-level business cases'],
                  ['Adjustability', 'Sliders per scenario (adoption, leadership, readiness)', 'Component weights configurable globally or per-org'],
                  ['Theoretical Basis', '70:20:10, change management research', 'Kirkpatrick-Phillips L2-L3, Brinkerhoff transfer model'],
                ].map(([dim, synth, roe], i) => (
                  <tr key={`compare-${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                    <td className="p-3 font-medium text-slate-700 border-b border-slate-100">{dim}</td>
                    <td className="p-3 border-b border-slate-100">{synth}</td>
                    <td className="p-3 border-b border-slate-100">{roe}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </ExpandableSection>

      {/* How Transfer Fits the Calculation Chain */}
      <ExpandableSection title="Where Transfer Fits in the Calculation Chain" icon={Scale} iconColor="text-purple-600" defaultOpen={false} testId="ref-chain-section">
        <div className="space-y-4 text-sm text-slate-700">
          <p>
            The transfer factor is one component of a multi-stage dampening chain that converts theoretical LEI improvement into realistic KPI impact:
          </p>
          <div className="flex flex-wrap items-center gap-2 p-4 bg-purple-50 border border-purple-200 rounded-lg text-xs">
            {[
              { label: 'LEI Improvement', desc: 'Raw % change' },
              { label: 'Weight', desc: 'LEI→KPI relationship' },
              { label: 'Elasticity', desc: 'Scenario assumption' },
              { label: 'Transfer Factor', desc: 'Synthetic OR ROE', highlight: true },
              { label: 'Scale Dampening', desc: 'Org size adjustment' },
              { label: 'Industry Modifier', desc: 'Sector-specific' },
              { label: 'Time Lag', desc: 'Realisation delay' },
              { label: 'Maturity Factor', desc: 'Current L&D maturity' },
            ].map((step, i, arr) => (
              <React.Fragment key={step.label}>
                <div className={cn("px-3 py-2 rounded-lg border text-center min-w-[100px]",
                  step.highlight ? "bg-emerald-100 border-emerald-300 ring-2 ring-emerald-400" : "bg-white border-purple-100"
                )}>
                  <p className={cn("font-bold", step.highlight ? "text-emerald-800" : "text-purple-800")}>{step.label}</p>
                  <p className="text-[10px] text-slate-500">{step.desc}</p>
                </div>
                {i < arr.length - 1 && <span className="text-purple-400 font-bold">x</span>}
              </React.Fragment>
            ))}
          </div>
          <p className="text-xs text-slate-500">
            <strong>Key insight:</strong> The ROE Composite <em>replaces</em> the Synthetic transfer factor — it does not stack on top of it. This prevents double-dampening and keeps results defensible.
          </p>
        </div>
      </ExpandableSection>

      {/* Weight Rationale */}
      <ExpandableSection title="Weight Rationale & Academic Grounding" icon={BookOpen} iconColor="text-slate-700" defaultOpen={false} testId="ref-weight-rationale-section">
        <div className="space-y-4 text-sm text-slate-700">
          <p>
            The default ROE weights (L2 = 25%, Motivation = 35%, L3 = 40%) are grounded in evaluation science and represent the relative predictive power of each level for business outcomes.
          </p>

          <div className="space-y-3">
            <div className="p-4 border border-slate-200 rounded-lg">
              <p className="font-bold text-slate-800 mb-2">Kirkpatrick-Phillips Evaluation Framework</p>
              <p className="text-xs text-slate-600 mb-2">
                Donald Kirkpatrick's four-level model (1959, updated 2016 as the New World Kirkpatrick Model) established that evaluation must progress from Reaction (L1) through Learning (L2), Behavior (L3), and Results (L4). Jack Phillips added Level 5 (ROI) in the 1980s to convert business results to financial returns. The model demonstrates that each level has increasing predictive validity for business impact, justifying higher weights for L3 over L2.
              </p>
              <div className="flex gap-2">
                <CitationLink href="https://www.kirkpatrickpartners.com/the-kirkpatrick-model/">Kirkpatrick Partners — The Kirkpatrick Model</CitationLink>
                <CitationLink href="https://www.accessplanit.com/en-gb/ap-blogs/phillips-roi-model">Phillips ROI Model Guide</CitationLink>
              </div>
            </div>

            <div className="p-4 border border-slate-200 rounded-lg">
              <p className="font-bold text-slate-800 mb-2">Brinkerhoff's High Impact Learning & Success Case Method</p>
              <p className="text-xs text-slate-600 mb-2">
                Robert Brinkerhoff's research demonstrated that only ~15% of training participants achieve full on-the-job application. His 40/20/40 model allocates 40% of impact to pre-training preparation, 20% to the training event, and 40% to post-training support — closely mirroring the weight given to L3 (behavior transfer) in the ROE Composite. The Success Case Method provides the qualitative complement to quantitative L3 measurement.
              </p>
              <div className="flex gap-2">
                <CitationLink href="https://wearehpc.com/professor-brinkerhoffs-insights-into-learning-transfer-impact/">Brinkerhoff Transfer Research</CitationLink>
                <CitationLink href="https://www.watershedlrs.com/blog/learning-evaluation/brinkerhoff-method/">Success Case Method</CitationLink>
              </div>
            </div>

            <div className="p-4 border border-slate-200 rounded-lg">
              <p className="font-bold text-slate-800 mb-2">70:20:10 Framework</p>
              <p className="text-xs text-slate-600 mb-2">
                Originated from the Center for Creative Leadership's research with 200 executives (1980s), the 70:20:10 model posits that 70% of learning comes from on-the-job experience, 20% from social interaction, and 10% from formal training. This underpins why transfer measurement (what happens after the classroom) is more predictive than learning measurement (what happens in the classroom) — supporting higher L3 weighting.
              </p>
              <div className="flex gap-2">
                <CitationLink href="https://www.702010institute.com/702010-model/">70:20:10 Institute</CitationLink>
                <CitationLink href="https://www.ccl.org/articles/leading-effectively-articles/70-20-10-rule/">Center for Creative Leadership</CitationLink>
              </div>
            </div>

            <div className="p-4 border border-slate-200 rounded-lg">
              <p className="font-bold text-slate-800 mb-2">People Engine (PE) — Transfer as a System</p>
              <p className="text-xs text-slate-600 mb-2">
                Seertech's People Engine module models transfer as a system-level outcome, not an individual-level event. PE integrates capability (measured via L2), retention (workforce stability), motivation (willingness to apply), and LEI quality into a composite workforce performance score. The ROE Composite directly feeds PE Validated calculations, creating a closed-loop from evaluation data to business impact projection.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => navigate('/people-engine/reference')}>
                  PE Reference <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
                <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => navigate('/roe-reference')}>
                  ROE Reference <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            </div>

            <div className="p-4 border border-slate-200 rounded-lg">
              <p className="font-bold text-slate-800 mb-2">LTEM — Learning-Transfer Evaluation Model</p>
              <p className="text-xs text-slate-600 mb-2">
                Will Thalheimer's LTEM provides 8 tiers of evaluation granularity beyond Kirkpatrick, emphasising task competence and real-world decision-making over knowledge recall. This supports the ROE Composite's emphasis on L3 (observed behavior) over L2 (test scores), as LTEM demonstrates that higher-fidelity evaluation levels correlate more strongly with workplace performance.
              </p>
              <CitationLink href="https://www.worklearning.com/ltem/">LTEM — Will Thalheimer</CitationLink>
            </div>
          </div>
        </div>
      </ExpandableSection>

      {/* Decision Framework */}
      <Card className="border-2 border-indigo-100 bg-gradient-to-br from-indigo-50/30 to-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-indigo-600" />
            Which Method Should You Use?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-slate-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border-2 border-amber-200 bg-amber-50/50 rounded-lg">
              <p className="font-bold text-amber-800 mb-2 flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" /> Use Synthetic when:</p>
              <ul className="space-y-1 text-xs text-amber-700 list-disc list-inside">
                <li>You're modelling a new intervention that hasn't been delivered yet</li>
                <li>ROE data collection infrastructure isn't in place</li>
                <li>You need quick directional estimates for planning</li>
                <li>Scenarios are exploratory rather than for formal business cases</li>
              </ul>
            </div>
            <div className="p-4 border-2 border-emerald-200 bg-emerald-50/50 rounded-lg">
              <p className="font-bold text-emerald-800 mb-2 flex items-center gap-2"><FlaskConical className="h-4 w-4" /> Use ROE Composite when:</p>
              <ul className="space-y-1 text-xs text-emerald-700 list-disc list-inside">
                <li>All 3 ROE data types are collected (L2 + Motivation + L3)</li>
                <li>Building a business case for senior leadership or board</li>
                <li>Comparing projected vs actual impact requires empirical grounding</li>
                <li>You want maximum defensibility in your ROI calculations</li>
              </ul>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            <strong>Recommendation:</strong> Start with Synthetic for initial scenario modelling. As your L&D function matures and ROE data collection improves, switch to ROE Composite for production business cases. The system makes this transition seamless — toggle once in Org Defaults, and all new scenarios automatically use the empirical method.
          </p>
        </CardContent>
      </Card>

      {/* Cross-References */}
      <Card className="bg-slate-50">
        <CardContent className="p-4">
          <p className="text-sm font-semibold text-slate-700 mb-3">Related Reference Materials</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="text-xs" onClick={() => navigate('/roe-reference')}>ROE Reference</Button>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => navigate('/people-engine/reference')}>PE Reference</Button>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => navigate('/lei-reference')}>LEI Reference</Button>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => navigate('/roe-health')}>ROE Health</Button>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-200">
            <p className="text-[10px] text-slate-400 font-medium mb-2">External References</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px]">
              <CitationLink href="https://www.kirkpatrickpartners.com/the-kirkpatrick-model/">Kirkpatrick Model</CitationLink>
              <CitationLink href="https://www.accessplanit.com/en-gb/ap-blogs/phillips-roi-model">Phillips ROI Model</CitationLink>
              <CitationLink href="https://wearehpc.com/professor-brinkerhoffs-insights-into-learning-transfer-impact/">Brinkerhoff Transfer Research</CitationLink>
              <CitationLink href="https://www.702010institute.com/702010-model/">70:20:10 Institute</CitationLink>
              <CitationLink href="https://www.ccl.org/articles/leading-effectively-articles/70-20-10-rule/">Center for Creative Leadership</CitationLink>
              <CitationLink href="https://www.worklearning.com/ltem/">LTEM (Thalheimer)</CitationLink>
              <CitationLink href="https://www.kirkpatrickpartners.com/blog/evaluation-in-2025/">Evaluation in 2025</CitationLink>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    </PageShell>
  );
}
